/**
 * @file SystemLogAlerts.js
 * @version 1.3.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-19
 * @modified 2026-10-26
 *
 * The alerting half of the admin logger: everything in the system's logs that
 * looks like it needs action, so an admin doesn't have to scroll to find it.
 *
 * Shared by two hosts — the WorkerCronLogsWidget's default view (variant
 * "terminal") and the status-bar "Issues Logged" modal (variant "modal") —
 * so the two can never drift apart.
 *
 * Alerts arrive from GET /api/system-logs/alerts already severity-ranked.
 * They are grouped here by source+kind+severity because one recurring fault
 * writes one log row per run: the nightly credit-note allocation import
 * erroring on the same notes presents as ~68 rows of the same problem. The
 * group is the unit an admin actually reasons about, so it is also the unit
 * they can mark actioned.
 *
 * "Actioned" is recorded through the existing review-flag mechanism
 * (audit.review_acknowledgements via /api/review-flags/*), the same table the
 * till and statement flags use — not a logger-specific one.
 *
 * An entry whose details carry a `note` / `fix_prompt` (the credit allocation
 * check's warning does) has it shown once at the top of its open group, from
 * the newest entry, as text with a copy button (LogEntryNote), and left out of
 * the JSON block underneath.
 *
 * 1.3.0 (2026-10-20, Cam — "If Xero differs I need to be offered to accept
 * the Xero version from the admin issues log please"): a "Xero PO changes"
 * entry lists each PO Xero changed, with "Accept Xero version…" and "Keep
 * ours…" (XeroVersionDialog — what will change, line by line, confirmed
 * there). An answered PO shows who answered it; once every PO of an entry is
 * answered, the entry is marked actioned.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_URL } from '../config';
import LoadingSpinner from './LoadingSpinner';
import LogEntryNote, { noteOf, withoutNote } from './LogEntryNote';
import XeroVersionDialog from './XeroVersionDialog';
import { useSlideOutButton } from './SlideOutButton';
import './SystemLogAlerts.css';

const ANSWER_LABEL = { accepted: 'Xero version accepted', kept: 'Ours kept', current: 'Our copy holds this Xero version',
  // Deleted in Xero and archived here since (2026-10-26).
  archived: 'Archived here, deleted in Xero' };

/** The POs a "Xero PO changes" entry lists, each with its answer or the two actions. */
function PoChangeRows({ alert, onReview }) {
  const rows = Array.isArray(alert?.details?.changed_pos) ? alert.details.changed_pos : [];
  useSlideOutButton([rows.length, rows.filter(r => r.answer).length]);
  if (alert?.kind !== 'xero_po_changes' || !rows.length) return null;
  return (
    <div className="sla-po-changes">
      {rows.map((c) => (
        <div key={`${c.id}-${c.xero_updated || ''}`} className={`sla-po-change ${c.answer ? 'is-answered' : ''}`}>
          <div className="sla-po-change-text">
            <strong>{c.number || `PO #${c.id}`}</strong>
            {c.supplier ? <span className="sla-po-change-supplier"> · {c.supplier}</span> : null}
            {c.changed_by?.user ? <span className="sla-po-change-supplier"> · changed in Xero by {c.changed_by.user}</span> : null}
            <div className="sla-po-change-summary">{c.summary || 'Changed in Xero'} <span className="sla-po-change-dir">(ours → Xero's)</span></div>
          </div>
          {c.answer ? (
            <span className="sla-actioned-tag">
              {ANSWER_LABEL[c.answer.state] || 'answered'}{c.answer.by ? ` · ${c.answer.by}` : ''}
            </span>
          ) : c.id ? (
            <div className="sla-po-change-actions">
              <button type="button" className="btn-slideout" onClick={() => onReview(c, false)} data-write-action
                title="See what accepting Xero's version would change, then confirm">
                <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_download</span></div>
                <span className="btn-label">Accept Xero version…</span>
              </button>
              <button type="button" className="btn-slideout" onClick={() => onReview(c, true)} data-write-action
                title="Keep our copy as it is — see Xero's version first">
                <div className="icon-wrapper"><span className="material-symbols-rounded">do_not_disturb_on</span></div>
                <span className="btn-label">Keep ours…</span>
              </button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

const SOURCE_META = {
  sync_history:   { label: 'Scheduled sync',  icon: 'sync' },
  change_log:     { label: 'Audit trail',     icon: 'history' },
  email_messages: { label: 'Email delivery',  icon: 'mail' },
  email_queue:    { label: 'Statement email', icon: 'outgoing_mail' },
  backfill:       { label: 'Backfill job',    icon: 'cloud_sync' },
  epos_sweep:     { label: 'Till sweep',      icon: 'point_of_sale' },
  ai_usage:       { label: 'Claude API',      icon: 'auto_awesome' },
  job_monitor:    { label: 'Scheduled job',   icon: 'schedule' }
};

function pad(n) { return String(n).padStart(2, '0'); }

function formatStamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function relative(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * Falls back to a compact rendering of the details blob when the server
 * didn't supply a summary — sync_history rows carry their meaning in the
 * jsonb rather than in a text column.
 */
function describe(alert) {
  if (alert.summary) return alert.summary;
  const d = alert.details;
  if (!d || typeof d !== 'object') return alert.kind;
  if (d.error) return `ERROR: ${String(d.error).slice(0, 200)}`;
  const parts = [];
  const show = ['errored', 'errors', 'date_errors', 'orphaned', 'invoicesBlocked',
    'processed', 'imported', 'skipped', 'added', 'updated', 'total'];
  for (const k of show) {
    if (d[k] != null && typeof d[k] !== 'object') parts.push(`${k}=${d[k]}`);
  }
  return parts.length ? parts.join(' ') : alert.kind;
}

function SystemLogAlerts({ variant = 'terminal', onCountChange, autoRefreshMs = 60000 }) {
  const [alerts, setAlerts] = useState([]);
  const [counts, setCounts] = useState({ err: 0, warn: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showActioned, setShowActioned] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [openDetail, setOpenDetail] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  // The changed PO being reviewed in XeroVersionDialog: { change, keep }.
  const [reviewing, setReviewing] = useState(null);
  const lastAlerts = useRef([]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/system-logs/alerts?includeActioned=${showActioned ? 'true' : 'false'}`,
        { credentials: 'include' }
      );
      if (res.status === 403) { setError('Forbidden — admin only.'); setLoading(false); return; }
      if (!res.ok) { setError(`HTTP ${res.status}`); setLoading(false); return; }
      const data = await res.json();
      setAlerts(data.alerts || []);
      lastAlerts.current = data.alerts || [];
      setCounts(data.counts || { err: 0, warn: 0 });
      setError(null);
      if (onCountChange) onCountChange(data.total || 0);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [showActioned, onCountChange]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  useEffect(() => {
    if (!autoRefreshMs) return undefined;
    const handle = setInterval(fetchAlerts, autoRefreshMs);
    return () => clearInterval(handle);
  }, [autoRefreshMs, fetchAlerts]);

  // One recurring fault can be dozens of rows; the group is what an admin
  // reasons about, so it is the unit shown and the unit actioned.
  const groups = useMemo(() => {
    const idx = new Map();
    for (const a of alerts) {
      const key = `${a.source}|${a.kind}|${a.severity}`;
      let g = idx.get(key);
      if (!g) {
        g = { key, source: a.source, kind: a.kind, severity: a.severity, items: [], newest: null };
        idx.set(key, g);
      }
      g.items.push(a);
      if (!g.newest || new Date(a.ts) > new Date(g.newest)) g.newest = a.ts;
    }
    return [...idx.values()].sort((x, y) => {
      if (x.severity !== y.severity) return x.severity === 'err' ? -1 : 1;
      return new Date(y.newest || 0) - new Date(x.newest || 0);
    });
  }, [alerts]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const postFlags = async (path, items) => {
    const res = await fetch(`${API_URL}/api/review-flags/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `HTTP ${res.status}`);
    }
  };

  const actionItems = async (busy, items, reinstate = false) => {
    setBusyKey(busy);
    try {
      const payload = items.map(i => ({ flagType: i.flagType, entityKey: i.entityKey }));
      await postFlags(reinstate ? 'unacknowledge-bulk' : 'acknowledge-bulk', payload);
      await fetchAlerts();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const totalOutstanding = alerts.filter(a => !a.acknowledged).length;

  // A changed PO answered (accepted / kept): re-read, and mark actioned every
  // "Xero PO changes" entry whose POs are all answered now.
  const afterAnswer = async () => {
    await fetchAlerts();
    const done = (lastAlerts.current || []).filter(a => a.kind === 'xero_po_changes' && a.details?.all_answered && !a.acknowledged && a.flagType);
    if (done.length) await actionItems('xero-po-changes', done);
  };

  return (
    <div className={`system-log-alerts variant-${variant}`}>
      <div className="sla-toolbar">
        <div className="sla-tally">
          <span className="sla-pill sla-pill-err">
            <span className="material-symbols-rounded">error</span>{counts.err || 0}
          </span>
          <span className="sla-pill sla-pill-warn">
            <span className="material-symbols-rounded">warning</span>{counts.warn || 0}
          </span>
          <span className="sla-tally-text">
            {totalOutstanding === 0 ? 'nothing outstanding' : `${totalOutstanding} outstanding`}
          </span>
        </div>
        <div className="sla-toolbar-right">
          <span className="sla-toggle-label">Show actioned</span>
          <div className="checkbox-container">
            <div className="check light">
              <input
                id={`sla-show-actioned-${variant}`}
                type="checkbox"
                checked={showActioned}
                onChange={(e) => setShowActioned(e.target.checked)}
              />
              <label htmlFor={`sla-show-actioned-${variant}`}></label>
            </div>
          </div>
          <button type="button" className="btn-icon sla-refresh" onClick={fetchAlerts} title="Refresh">
            <span className="material-symbols-rounded">refresh</span>
          </button>
        </div>
      </div>

      <div className="sla-body">
        {loading && alerts.length === 0 ? (
          <div className="sla-empty"><LoadingSpinner /></div>
        ) : error ? (
          <div className="sla-empty sla-error">
            <span className="material-symbols-rounded">error</span> {error}
          </div>
        ) : groups.length === 0 ? (
          <div className="sla-empty sla-clear">
            <span className="material-symbols-rounded">check_circle</span>
            <div>
              <strong>No issues logged</strong>
              <p>Every log source is clean and every scheduled job has run on time.</p>
            </div>
          </div>
        ) : (
          groups.map((g) => {
            const meta = SOURCE_META[g.source] || { label: g.source, icon: 'article' };
            const open = openGroups.has(g.key);
            const outstanding = g.items.filter(i => !i.acknowledged);
            const actioned = g.items.filter(i => i.acknowledged);
            // Items arrive newest first, so this is the newest entry's note.
            const note = g.items.map(i => noteOf(i.details)).find(Boolean);
            return (
              <div key={g.key} className={`sla-group sev-${g.severity} ${open ? 'is-open' : ''}`}>
                <div className="sla-group-header">
                  <button
                    type="button"
                    className="sla-group-toggle"
                    onClick={() => toggleGroup(g.key)}
                    aria-expanded={open}
                  >
                    <span className="material-symbols-rounded sla-chevron" aria-hidden="true">
                      {open ? 'expand_more' : 'chevron_right'}
                    </span>
                    <span className={`sla-dot sev-dot-${g.severity}`} aria-hidden="true" />
                    <span className="sla-group-kind">{g.kind}</span>
                    <span className="sla-group-source">
                      <span className="material-symbols-rounded">{meta.icon}</span>
                      {meta.label}
                    </span>
                    <span className="sla-spacer" />
                    <span className="sla-group-when">{relative(g.newest)}</span>
                    <span className="sla-group-count">{g.items.length}</span>
                  </button>
                  {outstanding.length > 0 && (
                    <button
                      type="button"
                      className="btn-icon sla-action"
                      title={`Mark all ${outstanding.length} as actioned`}
                      disabled={busyKey === g.key}
                      onClick={() => actionItems(g.key, outstanding)}
                    >
                      <span className="material-symbols-rounded">
                        {busyKey === g.key ? 'progress_activity' : 'task_alt'}
                      </span>
                    </button>
                  )}
                  {outstanding.length === 0 && actioned.length > 0 && (
                    <button
                      type="button"
                      className="btn-icon sla-action sla-action-undo"
                      title={`Reinstate all ${actioned.length}`}
                      disabled={busyKey === g.key}
                      onClick={() => actionItems(g.key, actioned, true)}
                    >
                      <span className="material-symbols-rounded">
                        {busyKey === g.key ? 'progress_activity' : 'undo'}
                      </span>
                    </button>
                  )}
                </div>

                {open && (
                  <div className="sla-group-body">
                    {note && <LogEntryNote note={note.note} prompt={note.prompt} />}
                    {g.items.map((a) => (
                      <div key={a.uid} className={`sla-item ${a.acknowledged ? 'is-actioned' : ''}`}>
                        <button
                          type="button"
                          className="sla-item-toggle"
                          onClick={() => setOpenDetail(openDetail === a.uid ? null : a.uid)}
                          aria-expanded={openDetail === a.uid}
                        >
                          <span className="sla-item-ts">{formatStamp(a.ts)}</span>
                          <span className="sla-item-msg">{describe(a)}</span>
                          {a.acknowledged && (
                            <span className="sla-actioned-tag">
                              actioned{a.acknowledgedByName ? ` · ${a.acknowledgedByName}` : ''}
                            </span>
                          )}
                          <span className="material-symbols-rounded sla-item-caret" aria-hidden="true">
                            {openDetail === a.uid ? 'expand_more' : 'chevron_right'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`btn-icon sla-action ${a.acknowledged ? 'sla-action-undo' : ''}`}
                          title={a.acknowledged ? 'Reinstate this alert' : 'Mark as actioned'}
                          disabled={busyKey === a.uid}
                          onClick={() => actionItems(a.uid, [a], a.acknowledged)}
                        >
                          <span className="material-symbols-rounded">
                            {busyKey === a.uid ? 'progress_activity' : (a.acknowledged ? 'undo' : 'task_alt')}
                          </span>
                        </button>
                        <PoChangeRows alert={a} onReview={(change, keep) => setReviewing({ change, keep })} />
                        {openDetail === a.uid && (
                          <pre className="sla-item-details">
                            {JSON.stringify(
                              { actor: a.actor, entity: a.entityIdentifier, ...withoutNote(a.details) },
                              null, 2
                            )}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {reviewing && (
        <XeroVersionDialog
          poId={reviewing.change.id}
          poNumber={reviewing.change.number}
          xeroUpdated={reviewing.change.xero_updated || null}
          keepOffered
          onClose={() => setReviewing(null)}
          onDone={afterAnswer}
        />
      )}
    </div>
  );
}

export default SystemLogAlerts;
